import { useCallback, useState } from "react";
import * as services from "../services/importDataApi";
import { useDispatch, useSelector } from "react-redux";
import { setIsLoading } from "../slices/companySlice";
import { generateRandomString } from "utils/helper";
import { Button, Card, Col, Flex, Form, List, message, Modal, Row } from "antd";
import { useTranslation } from "react-i18next";
import Dragger from "antd/es/upload/Dragger";
import { ReactComponent as UploadIcon } from "../../../assets/icon/uploadIcon.svg";
import { ReactComponent as ExcelIcon } from "../../../assets/icon/excelIcon.svg";
import { ReactComponent as DownloadIcon } from "../../../assets/icon/downloadIcon.svg";

const useCompanyImportData = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [downloadType, setDownloadType] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [fileList, setFileList] = useState([])
    const IMPORT_TITLE_KEY = {
        checkin: "import_data.label.import_timestamp",
        accumulate: "import_data.label.import_annual_accumulate",
        fund: "import_data.label.import_fund",
    };

    const TEMPLATE_FILENAME = {
        checkin: "employee-check-in-data-template.xlsx",
        accumulate: "employee-accumulate-template.xlsx",
        fund: "employee-fund-import-template.xlsx",
    };
    const openModal = (type) => {
        setDownloadType(type);
        setIsModalOpen(true);
    };

    async function callUpload() {
        Modal.confirm({
            title: t("dialog.confirmation.header"),
            content: (i18n.language === "en"
                ? <span>Are you sure confirm to upload ?</span>
                : <span>คุณแน่ใจที่จะยืนยันการอัปโหลดหรือไม่ ?</span>
            ),
            async onOk() {
                const file = fileList?.[0];
                let res;

                if (downloadType === "accumulate") {
                    res = await services.importEmployeeAccumulateData({ file });
                } else if (downloadType === "checkin") {
                    res = await services.importEmployeeCheckInData({ file });
                } else if (downloadType === "fund") {
                    res = await services.importFundMembership({ file });
                } else {
                    return;
                }

                if (res?.data?.status?.code == "200") {
                    setIsModalOpen(false);
                    setFileList([]);
                    message.success(t("common.success"));
                }
            },
            okText: t("common.confirm").toUpperCase(),
            cancelText: t("common.cancel").toUpperCase(),
        });
    }

    function xlsxIcon() {
        return <svg width="30" height="30" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.4843 1.84619H9.84619V3.6825H11.7114V4.93235H9.84619V6.15388H11.6923V7.38465H9.84619V8.61542H11.6923V9.84619H9.84619V11.077H11.6923V12.3077H9.84619V14.1539H15.4843C15.768 14.1539 16 13.9133 16 13.6185V2.38158C16 2.08681 15.768 1.84619 15.4843 1.84619ZM14.7693 12.3077H12.3077V11.077H14.7693V12.3077ZM14.7693 9.84619H12.3077V8.61542H14.7693V9.84619ZM14.7693 7.38465H12.3077V6.15388H14.7693V7.38465ZM14.7693 4.92311H12.3077V3.69235H14.7693V4.92311Z" fill="#1E6E42" />
            <path d="M0 1.77785V14.2228L9.23077 16V0L0 1.77785ZM5.83877 11.1262L4.76492 9.096C4.72431 9.02031 4.68246 8.88123 4.63877 8.67877H4.62215C4.60185 8.77415 4.55385 8.91938 4.47815 9.11385L3.40062 11.1262H1.72738L3.71385 8.00985L1.89662 4.89292H3.60492L4.496 6.76185C4.56554 6.90954 4.62769 7.08492 4.68308 7.28738H4.70031C4.73538 7.16554 4.80062 6.98462 4.896 6.744L5.88677 4.89231H7.45108L5.58215 7.98277L7.50338 11.1255L5.83877 11.1262Z" fill="#1E6E42" />
        </svg>

    }

    const modalDownload = useCallback(async () => {
        try {
            dispatch(setIsLoading(true));

            const payload = { requestId: generateRandomString(), companyId: currentCompany };
            let res;
            if (downloadType === "accumulate") {
                res = await services.exportEmployeeAccumulateTemplate(payload);
            } else if (downloadType === "checkin") {
                res = await services.exportEmployeeCheckInTemplate(payload);
            } else if (downloadType === "fund") {
                res = await services.exportFundImportTemplate(payload);
            }

            if (res) {
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", TEMPLATE_FILENAME[downloadType]);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error("Error downloading template:", error);
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [dispatch, downloadType, currentCompany]);

    const CompanyImportModal = useCallback(
        () => {
            function fileSize(item) {
                if (item.size / 1000000 > 1) {
                    return (item.size / 1000000) + " mb"
                } else if (item.size / 1000 > 1) {
                    return (item.size / 1000) + " kb"
                } else {
                    return item.size + " byte"
                }
            }
            return <Modal
                footer={
                    <Row style={{ marginTop: "24px", justifyContent: "end", gap: 12 }}>
                        <Button onClick={() => {
                            setFileList([])
                            setIsModalOpen(false)
                        }} style={{ borderRadius: 8, padding: "8px 16px", width: "max-content", height: "max-content" }}>
                            {t('action.cancel')}
                        </Button>
                        <Button type="primary" onClick={() => {
                            callUpload()
                        }}
                            style={{ borderRadius: 8, padding: "8px 16px", width: "max-content", height: "max-content", backgroundColor: !fileList.length ? "" : "var(--button-color)" }}
                            disabled={!fileList.length}
                        >
                            {t('action.submit')}
                        </Button>

                    </Row>
                }
                title={
                    <Form.Item
                        colon={false}
                        label={<span style={{ fontSize: "16px", color: "#475467" }}>
                            {t(IMPORT_TITLE_KEY[downloadType])}
                        </span>}
                    />
                }
                destroyOnClose
                closable={false}
                open={isModalOpen}
                closeIcon={() => { }}
                onCancel={() => {
                    setFileList([])
                    setIsModalOpen(false)
                }}

                onOk={() => { callUpload() }}>
                <Dragger
                    fileList={fileList}
                    type="drag"
                    maxCount={1}
                    listType="picture"
                    showUploadList={true}
                    style={{ backgroundColor: "white", borderRadius: "12px" }}
                    onDrop={(e) => {
                        console.log("onDrop", e);
                    }}
                    itemRender={(e, file, fileList, action) => {
                        return <List dataSource={fileList}
                            renderItem={(item) => (
                                <List.Item key={1}>
                                    <List.Item.Meta
                                        avatar={xlsxIcon()}
                                        title={item.name}
                                        description={fileSize(item)}
                                    />
                                    <Button style={{
                                        border: "transparent"
                                    }} icon={"x"} onClick={() => { action.remove() }} >
                                    </Button>
                                </List.Item>
                            )}
                        />
                    }}
                    beforeUpload={(info) => {
                        return false;
                    }

                    }
                    onChange={(info) => {
                        if (info.file?.name && !info.file?.name?.endsWith("xlsx")) {
                            message.error("ระบบรองรับไฟล์ xlsx")
                            setFileList([])
                        } else if (info.file?.name) {
                            setFileList([info?.file])
                        }
                    }}
                    onRemove={() => {
                        console.log("onRemove");
                        setFileList([])
                    }}
                >
                    <Flex style={{
                        height: "20vh",
                        rowGap: "14px"
                    }} vertical justify="center" align="center">
                        <Flex style={{ width: "10%" }} ><UploadIcon /></Flex>
                        <Flex>
                            <span style={{ color: "#2CADE7", fontWeight: "bold" }}>{t("common.choose_file")}</span>
                            &nbsp;
                            <span style={{ color: "#475467" }}>{t("common.upload_dnd")}</span>
                        </Flex>

                    </Flex>
                </Dragger>
                {!fileList?.length > 0 &&
                    <Flex style={{
                        marginTop: "15px",
                        rowGap: "15px"
                    }} gap={5} vertical>
                        <span style={{ color: "#475467" }}>{t("common.file_support")}</span>

                        <Card
                            style={{ backgroundColor: "#F5F6F7", border: "1px solid #E4E7EC", borderRadius: "12px" }}
                            styles={{ body: { padding: "16px" } }}
                        >
                            <Row style={{ width: "100%" }}>
                                <Col span={16}>
                                    <Flex gap={16} align="center">
                                        <ExcelIcon />
                                        <Flex vertical>
                                            <span style={{ fontWeight: "bold", color: "#42526D" }}>{t("employee.label.blank_form")}</span>
                                            <span style={{ color: "#42526D" }}>{t("employee.label.template_description")}</span>
                                        </Flex>
                                    </Flex>
                                </Col>
                                <Col span={8} style={{ justifyItems: "self-end" }}>
                                    <Button type="default"
                                        style={{
                                            height: "40px",
                                            borderRadius: "8px",
                                            display: "flex",
                                            alignItems: "center"
                                        }}
                                        loading={isDownloading}
                                        disabled={isDownloading}
                                        onClick={() => { modalDownload() }}
                                        icon={
                                            !isDownloading ? (
                                                <DownloadIcon style={{ fontSize: 16 }} />
                                            ) : (
                                                <span style={{ width: 16, height: 16 }} />
                                            )
                                        }
                                    >
                                        <span style={{ color: "#59687F", fontWeight: "bold", whiteSpace: "nowrap" }}>{t("common.download")}</span>
                                    </Button>
                                </Col>
                            </Row>
                        </Card>

                    </Flex>
                }
            </Modal>
        },
        [callUpload, isModalOpen, t, downloadType],
    )

    return {
        openModal,
        CompanyImportModal,
    }
}
export default useCompanyImportData;